/**
 * Frontend runtime config — env only (Vite `VITE_*`), never hardcode hosts/sites.
 *
 * Production: leave VITE_FRAPPE_SITE / VITE_API_BASE empty; same-origin + window hostname.
 * Development: set values in `.env` (see `.env.example`).
 *
 * Backend public URLs (client_base_url, etc.) come from boot via site_config.
 */

function env(key: string, fallback = ''): string {
  const v = import.meta.env[key]
  if (v === undefined || v === null || v === '') return fallback
  return String(v)
}

/** SPA public path base (Vite `base` → import.meta.env.BASE_URL) */
export const appBase = import.meta.env.BASE_URL || '/'

/**
 * Optional absolute API origin (rare). Prefer same-origin reverse proxy in production.
 * Example dev: empty (Vite proxies /api). Example split deploy: https://api.example.com
 */
export const apiBase = env('VITE_API_BASE', '').replace(/\/$/, '')

/** Socket.io path (same-origin; Vite or nginx proxies to Frappe socketio) */
export const socketPath = env('VITE_SOCKET_PATH', '/socket.io')

/**
 * Multitenant site name for X-Frappe-Site-Name.
 * Prefer env in dev when browser host ≠ site folder name.
 * Otherwise: boot.site_name → window.site_name → location.hostname
 */
export function resolveSiteName(bootSite?: string): string {
  return (
    env('VITE_FRAPPE_SITE') ||
    bootSite ||
    window.site_name ||
    window.location.hostname
  )
}

/** Build API URL (relative by default). */
export function apiUrl(path: string): string {
  if (!path) return apiBase || ''
  if (/^https?:\/\//i.test(path)) return path
  const p = path.startsWith('/') ? path : `/${path}`
  return `${apiBase}${p}`
}

export function methodUrl(method: string): string {
  if (method.startsWith('/')) return apiUrl(method)
  return apiUrl(`/api/method/${method}`)
}

/** Config snapshot for debugging (no secrets). */
export function getPublicConfig(): Record<string, unknown> {
  return {
    appBase,
    apiBase: apiBase || null,
    socketPath,
    frappeSiteEnv: env('VITE_FRAPPE_SITE') || null,
    mode: import.meta.env.MODE,
  }
}
